import React, { useState, useRef } from 'react';
import { apiService } from '../services/api';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { useNotifications } from './NotificationSystem';
import { handleApiError } from '../utils/errorHandler';
import './ImportExport.css';

interface ImportResult {
  success: number;
  errors: number;
  total: number;
  errorDetails: string[];
}

interface ExportOptions {
  format: 'csv' | 'excel' | 'pdf';
  dateRange: {
    from: string;
    to: string;
  };
  includeAccounts: boolean;
  includeTransactions: boolean;
  includeReports: boolean;
}

const ImportExport: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'import' | 'export'>('import');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    format: 'csv',
    dateRange: {
      from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
      to: new Date().toISOString().split('T')[0]
    },
    includeAccounts: true,
    includeTransactions: true,
    includeReports: false
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showSuccess, showError } = useNotifications();

  // Import Functions
  const handleFileUpload = (file: File) => {
    setIsProcessing(true);
    setImportResult(null);

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    if (fileExtension === 'csv') {
      handleCSVImport(file);
    } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      handleExcelImport(file);
    } else {
      setImportResult({
        success: 0,
        errors: 1,
        total: 1,
        errorDetails: ['Unsupported file format. Please use CSV or Excel files.']
      });
      setIsProcessing(false);
    }
  };

  const handleCSVImport = (file: File) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: any) => {
        processImportData(results.data);
      },
      error: (error: any) => {
        setImportResult({
          success: 0,
          errors: 1,
          total: 1,
          errorDetails: [`CSV parsing error: ${error.message}`]
        });
        setIsProcessing(false);
      }
    });
  };

  const handleExcelImport = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        processImportData(jsonData);
      } catch (error) {
        setImportResult({
          success: 0,
          errors: 1,
          total: 1,
          errorDetails: [`Excel parsing error: ${error}`]
        });
        setIsProcessing(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const processImportData = async (data: any[]) => {
    let successCount = 0;
    let errorCount = 0;
    const errorDetails: string[] = [];

    for (const row of data) {
      try {
        // Determine if this is a transaction or account based on available fields
        if (row['Date'] || row['Entry Date'] || row['Transaction Date']) {
          await importTransaction(row);
          successCount++;
        } else if (row['Account Name'] || row['Name']) {
          await importAccount(row);
          successCount++;
        } else {
          errorCount++;
          errorDetails.push(`Row ${data.indexOf(row) + 1}: Unable to determine data type`);
        }
      } catch (error) {
        errorCount++;
        errorDetails.push(`Row ${data.indexOf(row) + 1}: ${error}`);
      }
    }

    setImportResult({
      success: successCount,
      errors: errorCount,
      total: data.length,
      errorDetails
    });
    setIsProcessing(false);
  };

  const importTransaction = async (row: any) => {
    const transactionData = {
      entryDate: row['Date'] || row['Entry Date'] || row['Transaction Date'] || new Date().toISOString().split('T')[0],
      accountId: 1, // Default account - in real implementation, you'd map this
      description: row['Description'] || row['Memo'] || row['Notes'] || '',
      amount: parseFloat(row['Amount'] || row['Value'] || '0'),
      type: determineTransactionType(row),
      category: row['Category'] || row['Type'] || 'Imported',
      narration: row['Narration'] || row['Notes'] || '',
      reference: row['Reference'] || row['ID'] || '',
      tags: row['Tags'] || ''
    };

    await apiService.createTransaction(transactionData);
  };

  const importAccount = async (row: any) => {
    // Note: Account creation via API not implemented yet
    // For now, just log the account data
    console.log('Account data to import:', {
      name: row['Account Name'] || row['Name'] || 'Imported Account',
      accountType: row['Type'] || row['Account Type'] || 'Asset',
      description: row['Description'] || '',
      openingBalance: parseFloat(row['Opening Balance'] || row['Balance'] || '0'),
      currentBalance: parseFloat(row['Current Balance'] || row['Balance'] || '0'),
      isActive: true
    });
  };

  const determineTransactionType = (row: any): number => {
    const amount = parseFloat(row['Amount'] || row['Value'] || '0');
    const type = (row['Type'] || row['Transaction Type'] || '').toLowerCase();
    
    if (type.includes('income') || type.includes('credit') || amount > 0) {
      return 1; // Income
    } else if (type.includes('expense') || type.includes('debit') || amount < 0) {
      return 2; // Expense
    } else {
      return 3; // Transfer
    }
  };

  // Export Functions
  const handleExport = async () => {
    setIsProcessing(true);
    
    try {
      switch (exportOptions.format) {
        case 'csv':
          await exportToCSV();
          break;
        case 'excel':
          await exportToExcel();
          break;
        case 'pdf':
          await exportToPDF();
          break;
      }
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const exportToCSV = async () => {
    const csvData: string[] = [];
    
    if (exportOptions.includeTransactions) {
      const transactions = await apiService.getTransactions({
        fromDate: exportOptions.dateRange.from,
        toDate: exportOptions.dateRange.to,
        pageSize: 1000
      });
      
      // CSV Headers
      csvData.push('ID,Date,Account,Description,Amount,Type,Category,Narration,Reference,Tags');
      
      // CSV Data
      transactions.transactions.forEach(t => {
        csvData.push([
          t.id,
          t.entryDate,
          t.accountName,
          `"${t.description.replace(/"/g, '""')}"`,
          t.amount,
          t.type === 1 ? 'Income' : t.type === 2 ? 'Expense' : 'Transfer',
          `"${(t.category || '').replace(/"/g, '""')}"`,
          `"${(t.narration || '').replace(/"/g, '""')}"`,
          `"${(t.reference || '').replace(/"/g, '""')}"`,
          `"${(t.tags || '').replace(/"/g, '""')}"`
        ].join(','));
      });
    }
    
    if (exportOptions.includeAccounts) {
      const stats = await apiService.getDashboardStats();
      csvData.push('\n\nAccount Data\nID,Name,Type,Balance,Monthly Change');
      stats.accountBalances.forEach(acc => {
        csvData.push([
          acc.id,
          `"${acc.name.replace(/"/g, '""')}"`,
          acc.accountType,
          acc.balance,
          acc.monthlyChange
        ].join(','));
      });
    }
    
    downloadFile(csvData.join('\n'), 'rokdahisab_export.csv', 'text/csv');
    showSuccess('Export Successful', 'CSV exported successfully!');
  };

  const exportToExcel = async () => {
    const workbook = XLSX.utils.book_new();
    
    if (exportOptions.includeTransactions) {
      const transactions = await apiService.getTransactions({
        fromDate: exportOptions.dateRange.from,
        toDate: exportOptions.dateRange.to,
        pageSize: 1000
      });
      
      const transactionData = transactions.transactions.map(t => ({
        ID: t.id,
        Date: t.entryDate,
        Account: t.accountName,
        Description: t.description,
        Amount: t.amount,
        Type: t.type === 1 ? 'Income' : t.type === 2 ? 'Expense' : 'Transfer',
        Category: t.category || '',
        Narration: t.narration || '',
        Reference: t.reference || '',
        Tags: t.tags || ''
      }));
      
      const ws = XLSX.utils.json_to_sheet(transactionData);
      XLSX.utils.book_append_sheet(workbook, ws, 'Transactions');
    }
    
    if (exportOptions.includeAccounts) {
      const stats = await apiService.getDashboardStats();
      const accountData = stats.accountBalances.map(acc => ({
        ID: acc.id,
        Name: acc.name,
        Type: acc.accountType,
        Balance: acc.balance,
        'Monthly Change': acc.monthlyChange
      }));
      
      const ws = XLSX.utils.json_to_sheet(accountData);
      XLSX.utils.book_append_sheet(workbook, ws, 'Accounts');
    }
    
    XLSX.writeFile(workbook, 'rokdahisab_export.xlsx');
    showSuccess('Export Successful', 'Excel file exported successfully!');
  };

  const exportToPDF = async () => {
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPosition = 20;
      
      // Add title
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('RokdaHisab Export Report', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;
      
      // Add date range
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Date Range: ${exportOptions.dateRange.from} to ${exportOptions.dateRange.to}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      
      pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 20;
      
      // Add transactions if selected
      if (exportOptions.includeTransactions) {
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Transactions', 20, yPosition);
        yPosition += 10;
        
        try {
          const transactions = await apiService.getTransactions({
            fromDate: exportOptions.dateRange.from,
            toDate: exportOptions.dateRange.to,
            pageSize: 1000
          });
          
          if (transactions.transactions.length > 0) {
            // Table headers
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Date', 20, yPosition);
            pdf.text('Description', 50, yPosition);
            pdf.text('Amount', 120, yPosition);
            pdf.text('Type', 150, yPosition);
            pdf.text('Category', 170, yPosition);
            yPosition += 8;
            
            // Table data
            pdf.setFont('helvetica', 'normal');
            transactions.transactions.forEach((transaction, index) => {
              if (yPosition > pageHeight - 20) {
                pdf.addPage();
                yPosition = 20;
              }
              
              const date = new Date(transaction.entryDate).toLocaleDateString();
              const description = transaction.description.length > 20 ? 
                transaction.description.substring(0, 20) + '...' : transaction.description;
              const amount = transaction.amount.toFixed(2);
              const type = transaction.type === 1 ? 'Income' : transaction.type === 2 ? 'Expense' : 'Transfer';
              const category = transaction.category || 'Uncategorized';
              
              pdf.text(date, 20, yPosition);
              pdf.text(description, 50, yPosition);
              pdf.text(amount, 120, yPosition);
              pdf.text(type, 150, yPosition);
              pdf.text(category, 170, yPosition);
              yPosition += 6;
            });
          } else {
            pdf.setFont('helvetica', 'normal');
            pdf.text('No transactions found for the selected date range.', 20, yPosition);
            yPosition += 10;
          }
        } catch (error) {
          pdf.setFont('helvetica', 'normal');
          pdf.text('Error loading transactions: ' + (error as Error).message, 20, yPosition);
          yPosition += 10;
        }
        
        yPosition += 10;
      }
      
      // Add accounts if selected
      if (exportOptions.includeAccounts) {
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Accounts', 20, yPosition);
        yPosition += 10;
        
        try {
          const stats = await apiService.getDashboardStats();
          
          if (stats.accountBalances.length > 0) {
            // Table headers
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Name', 20, yPosition);
            pdf.text('Type', 80, yPosition);
            pdf.text('Balance', 120, yPosition);
            pdf.text('Monthly Change', 160, yPosition);
            yPosition += 8;
            
            // Table data
            pdf.setFont('helvetica', 'normal');
            stats.accountBalances.forEach((account) => {
              if (yPosition > pageHeight - 20) {
                pdf.addPage();
                yPosition = 20;
              }
              
              pdf.text(account.name, 20, yPosition);
              pdf.text(account.accountType, 80, yPosition);
              pdf.text(account.balance.toFixed(2), 120, yPosition);
              pdf.text(account.monthlyChange.toFixed(2), 160, yPosition);
              yPosition += 6;
            });
          } else {
            pdf.setFont('helvetica', 'normal');
            pdf.text('No accounts found.', 20, yPosition);
            yPosition += 10;
          }
        } catch (error) {
          pdf.setFont('helvetica', 'normal');
          pdf.text('Error loading accounts: ' + (error as Error).message, 20, yPosition);
          yPosition += 10;
        }
      }
      
      // Save the PDF
      pdf.save('rokdahisab_export.pdf');
      showSuccess('Export Successful', 'PDF exported successfully!');
    } catch (error) {
      console.error('PDF export error:', error);
      const appError = handleApiError(error);
      showError('Export Error', appError.message);
    }
  };

  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Drag and Drop Handlers
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };


  return (
    <div className="import-export">
      <div className="import-export-header">
        <h1>Import & Export</h1>
        <p>Import data from other apps or export your RokdaHisab data</p>
      </div>

      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'import' ? 'active' : ''}`}
          onClick={() => setActiveTab('import')}
        >
          📥 Import Data
        </button>
        <button 
          className={`tab-button ${activeTab === 'export' ? 'active' : ''}`}
          onClick={() => setActiveTab('export')}
        >
          📤 Export Data
        </button>
      </div>

      {activeTab === 'import' && (
        <div className="import-section">
          <div className="import-instructions">
            <h3>Import Instructions</h3>
            <ul>
              <li>Supported formats: CSV, Excel (.xlsx, .xls)</li>
              <li>For transactions: Include columns like Date, Description, Amount, Type, Category</li>
              <li>For accounts: Include columns like Name, Type, Balance</li>
              <li>Download our template for the correct format</li>
            </ul>
          </div>

          <div 
            className={`drop-zone ${dragActive ? 'active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="drop-zone-content">
              <div className="drop-zone-icon">📁</div>
              <h3>Drop your file here or click to browse</h3>
              <p>Supports CSV and Excel files</p>
              <button className="browse-button">Choose File</button>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                handleFileUpload(e.target.files[0]);
              }
            }}
            style={{ display: 'none' }}
          />

          <div className="template-downloads">
            <h4>Download Templates</h4>
            <div className="template-buttons">
              <button 
                className="template-btn"
                onClick={() => {
                  const csvContent = 'Date,Description,Amount,Type,Category,Narration,Reference,Tags\n2024-01-01,Sample Transaction,100,Expense,Food,Lunch at restaurant,REF001,food,lunch';
                  downloadFile(csvContent, 'transaction_template.csv', 'text/csv');
                }}
              >
                📄 Transaction Template (CSV)
              </button>
              <button 
                className="template-btn"
                onClick={() => {
                  const csvContent = 'Name,Type,Description,Opening Balance,Current Balance\nSample Account,Asset,My bank account,1000,1000';
                  downloadFile(csvContent, 'account_template.csv', 'text/csv');
                }}
              >
                🏦 Account Template (CSV)
              </button>
            </div>
          </div>

          {isProcessing && (
            <div className="processing-indicator">
              <div className="spinner"></div>
              <p>Processing your file...</p>
            </div>
          )}

          {importResult && (
            <div className="import-result">
              <h3>Import Results</h3>
              <div className="result-summary">
                <div className="result-stat success">
                  <span className="stat-number">{importResult.success}</span>
                  <span className="stat-label">Successful</span>
                </div>
                <div className="result-stat error">
                  <span className="stat-number">{importResult.errors}</span>
                  <span className="stat-label">Errors</span>
                </div>
                <div className="result-stat total">
                  <span className="stat-number">{importResult.total}</span>
                  <span className="stat-label">Total</span>
                </div>
              </div>
              
              {importResult.errorDetails.length > 0 && (
                <div className="error-details">
                  <h4>Error Details:</h4>
                  <ul>
                    {importResult.errorDetails.map((error, index) => (
                      <li key={index} className="error-item">{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'export' && (
        <div className="export-section">
          <div className="export-options">
            <h3>Export Options</h3>
            
            <div className="option-group">
              <label>Export Format</label>
              <select 
                value={exportOptions.format}
                onChange={(e) => setExportOptions({
                  ...exportOptions,
                  format: e.target.value as 'csv' | 'excel' | 'pdf'
                })}
              >
                <option value="csv">CSV (Comma Separated Values)</option>
                <option value="excel">Excel (.xlsx)</option>
                <option value="pdf">PDF Report</option>
              </select>
            </div>

            <div className="option-group">
              <label>Date Range</label>
              <div className="date-range">
                <input
                  type="date"
                  value={exportOptions.dateRange.from}
                  onChange={(e) => setExportOptions({
                    ...exportOptions,
                    dateRange: { ...exportOptions.dateRange, from: e.target.value }
                  })}
                />
                <span>to</span>
                <input
                  type="date"
                  value={exportOptions.dateRange.to}
                  onChange={(e) => setExportOptions({
                    ...exportOptions,
                    dateRange: { ...exportOptions.dateRange, to: e.target.value }
                  })}
                />
              </div>
            </div>

            <div className="option-group">
              <label>Include Data</label>
              <div className="checkbox-group">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={exportOptions.includeTransactions}
                    onChange={(e) => setExportOptions({
                      ...exportOptions,
                      includeTransactions: e.target.checked
                    })}
                  />
                  <span>Transactions</span>
                </label>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={exportOptions.includeAccounts}
                    onChange={(e) => setExportOptions({
                      ...exportOptions,
                      includeAccounts: e.target.checked
                    })}
                  />
                  <span>Accounts</span>
                </label>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={exportOptions.includeReports}
                    onChange={(e) => setExportOptions({
                      ...exportOptions,
                      includeReports: e.target.checked
                    })}
                  />
                  <span>Reports & Analytics</span>
                </label>
              </div>
            </div>

            <button 
              className="export-button"
              onClick={handleExport}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <div className="spinner"></div>
                  Exporting...
                </>
              ) : (
                <>
                  📤 Export Data
                </>
              )}
            </button>
          </div>

        </div>
      )}
    </div>
  );
};

export default ImportExport;
